import { useLocaleState } from "ra-core";
import { useMemo } from "react";

import { useConfigurationContext } from "../root/ConfigurationContext";

/**
 * Formats money in the configured currency: with cents by default, rounded and
 * compact (`R$ 12 mil`) for chart axes and KPI cards when `compact` is set.
 */
export const useFormatMoney = () => {
  const [locale = "en"] = useLocaleState();
  const { currency } = useConfigurationContext();
  return useMemo(() => {
    const full = new Intl.NumberFormat(locale, { style: "currency", currency });
    const compact = new Intl.NumberFormat(locale, {
      style: "currency",
      currency,
      notation: "compact",
      maximumFractionDigits: 1,
    });
    return (amount: number | null | undefined, isCompact = false) =>
      (isCompact ? compact : full).format(amount ?? 0);
  }, [locale, currency]);
};

/** Formats a 0..1 ratio as a percentage */
export const useFormatPercent = () => {
  const [locale = "en"] = useLocaleState();
  return useMemo(() => {
    const format = new Intl.NumberFormat(locale, {
      style: "percent",
      maximumFractionDigits: 1,
    });
    return (ratio: number | null | undefined) =>
      ratio == null ? "–" : format.format(ratio);
  }, [locale]);
};
